<script setup>
import {getCurrentInstance, onBeforeMount, onMounted, reactive, ref} from 'vue'
import {Feedback, State, clone, deny, titlecase} from '@/paks/vu-app'
import {Expression} from '@/paks/js-expression'
import WidgetData from './WidgetData.vue'
import WidgetActions from './WidgetActions.vue'
import WidgetProperties from './WidgetProperties.vue'
import WidgetPresentation from './WidgetPresentation.vue'
import UUID from '@/paks/js-uuid'
import Json5 from 'json5'

const {proxy: self} = getCurrentInstance()

const props = defineProps({
    id: String,
    board: Object,
    modelValue: Object,
})

/*
    const model = defineModel()
    model.value...
*/

const page = reactive({
    applying: false,
    clouds: State.cache.clouds.sort(sortByName),
    editor: {canSave: true},
    fields: null,
    hasActions: false,
    ready: false,
    rules: [],
    saving: false,
    tab: null,
    widget: {
        cloudId: undefined,
        dimensions: null,
        field: null,
        fields: null,
        namespace: null,
        metric: null,
        model: null,
        statistic: null,
        type: null,
    },
})

const confirm = ref(null)
const form = ref(null)

const emit = defineEmits(['apply', 'close', 'remove', 'update:modelValue'])
defineExpose({page})

onBeforeMount(() => {
    let route = self.$route
    page.tab = State.app.tab[route.path] || 'properties'
})

onMounted(async () => {
    let clouds = clone(State.cache.clouds)
    if (State.config.builder) {
        clouds.push({id: 'builder', name: 'Builder', type: 'service'})
    }
    if (State.config.features.widget?.actions) {
        page.hasActions = true
    }
    clouds.forEach((c) => (c.name = `${titlecase(c.type)}: ${c.name}`))
    page.clouds = clouds.sort(sortByName)
    page.widget = clone(props.modelValue)
    page.ready = true
})

function tabChange(tab) {
    let route = self.$route
    State.app.tab[route.path] = tab
}

function copy() {
    let widget = clone(page.widget)
    delete widget.id
    widget = makeWidget(widget)
    emit('apply', widget)
    emit('close')
}

function preSave() {
    let widget = page.widget
    let expression = new Expression()
    for (let action of widget.actions || []) {
        if (action.conditions) {
            for (let cond of action.conditions) {
                if (cond.expression) {
                    try {
                        expression.parse(cond.expression)
                    } catch (err) {
                        throw new Error(`Invalid action expression`)
                    }
                }
                if (cond.params) {
                    try {
                        Json5.parse(cond.params)
                    } catch (err) {
                        throw new Error(`Invalid action params`)
                    }
                }
            }
        }
    }
    if (widget.type == 'image' || widget.type == 'audio' || widget.type == 'video') {
        if (widget.url?.indexOf('{') == 0 || widget.url?.indexOf('[') == 0) {
            try {
                Json5.parse(widget.url)
            } catch (err) {
                throw new Error(`Invalid image URL map or list`)
            }
        }
    }
}

function save() {
    apply()
    State.app.setNeedSave(props.board)
    emit('close')
}

function applyChange() {
    apply()
    State.app.setNeedSave(props.board)
}

function apply() {
    preSave()

    let widget = page.widget
    page.applying = true
    if (widget.type == 'set') {
        let name = 'Default'
        let cloudId = widget.cloudId
        let set = State.app.widgetSets.find((s) => s.name == name)
        for (let [key, widget] of Object.entries(set.widgets)) {
            if (widget.enable === false) continue
            widget = clone(widget)
            let cloud = State.get('Cloud', cloudId)
            if (cloud) {
                widget.title = `${cloud.name} ${widget.title || widget.name || ''}`
                widget.cloudId = cloudId
            }
            widget = makeWidget(widget)
            emit('apply', widget)
            emit('update:modelValue', widget)
        }
        Feedback.info(`Widget Set Created`)
    } else {
        widget = makeWidget(widget)
        emit('apply', widget)
        emit('update:modelValue', widget)
    }
    page.widget = widget
    page.applying = false
}

function makeWidget(widget) {
    if (widget._new && !widget.title && !widget.header) {
        if (widget.metric) {
            widget.header = `${widget.namespace} ${widget.metric}`
        } else if (widget.model) {
            widget.header = `${widget.model} ${widget.field || ''}`
        }
        if (widget.type == 'toolbar') {
            if (widget.css?.length == 0) {
                widget.css = [{property: 'background', value: 'white'}]
            }
        }
    }
    delete widget._new
    //  These fixes are needed for Hub stored dashboards
    if (!widget.namespace || widget.namespace == 'None') {
        widget.namespace = widget.metric = widget.dimensions = widget.statistic = null
    }
    if (widget.framed === '') {
        widget.framed = null
    }
    //  TODO - refactor - should have a single field to test (noinput)
    if (widget.type == 'audio') {
        widget = deny(widget, ['fields', 'range', 'value'])
    } else if (widget.type == 'image' || widget.type == 'video') {
        widget = deny(widget, ['fields', 'range', 'value', 'text'])
    } else {
        if (
            !(
                widget.input == 'form' ||
                widget.type == 'label' ||
                widget.input == 'button' ||
                widget.input == 'icon'
            )
        ) {
            delete widget.text
        }
        if (widget.type == 'toolbar' && !widget.id) {
            // No frame
            widget.framed = false
        }
        delete widget.url
        if (widget.type != 'input') {
            delete widget.input
        }
        if (widget.type == 'metric') {
            // Table of metric dimensions
            delete widget.dimensions
        }
    }
    if (!widget.id) {
        widget.id = UUID()
    }
    if (widget.min != null) {
        widget.min = widget.min ? widget.min - 0 : 0
    }
    if (widget.max != null) {
        widget.max = widget.max ? widget.max - 0 : 0
    }
    if (widget.pageSize) {
        widget.pageSize = widget.pageSize - 0
    }
    for (let action of widget.actions || []) {
        if (action && action.conditions) {
            if (action.conditions.length == 0 || !action.conditions[0]?.expression) {
                delete action.conditions
            }
            delete action._index
            delete action._select
        }
    }
    if (widget.anchor) {
        if (widget.anchor.horizontalOffset) {
            widget.anchor.horizontalOffset = widget.anchor.horizontalOffset
                .toString()
                .replace(/px$/, '')
        }
        if (widget.anchor.verticalOffset) {
            widget.anchor.verticalOffset = widget.anchor.verticalOffset
                .toString()
                .replace(/px$/, '')
        }
    }
    return widget
}

async function deleteWidget() {
    if (!page.widget.id) {
        return
    }
    if (!(await confirm.value.ask(`Do you want to delete the widget?`))) {
        return
    }
    State.app.setNeedSave(props.board)
    emit('remove', page.widget)
    emit('close')
}

function sortByName(a, b) {
    if (a.name < b.name) {
        return -1
    } else if (b.name < a.name) {
        return 1
    }
    return 0
}
</script>

<template>
    <vu-form
        ref="form"
        class="widget-edit"
        help="/doc/ui/dashboard/widget-edit.html"
        :options="{enter: false}"
        :data="page"
        :save="save"
        :title="`${page.widget.id ? 'Modify' : 'Add'} Widget`">
        <v-container fluid class="pa-0">
            <v-tabs v-model="page.tab" ref="tabs" class="widget-tabs mb-5" fixed-tabs @update:modelValue="tabChange">
                <v-tab value="properties">Properties</v-tab>
                <v-tab value="data">Data</v-tab>
                <v-tab value="actions" v-if="page.hasActions">Actions</v-tab>
                <v-tab value="presentation">Presentation</v-tab>
            </v-tabs>

            <v-window v-if="page.ready" v-model="page.tab" class="pt-4">
                <v-window-item value="properties">
                    <WidgetProperties v-model="page.widget" :board="props.board" :parent="page.editor" />
                </v-window-item>

                <v-window-item value="data">
                    <WidgetData v-model="page.widget" :board="props.board" :parent="page.editor" />
                </v-window-item>

                <v-window-item value="actions" v-if="page.hasActions">
                    <WidgetActions
                        v-model="page.widget"
                        :board="props.board"
                        :parent="page.editor" 
                    />
                </v-window-item>
                <v-window-item value="presentation">
                    <WidgetPresentation
                        v-model="page.widget"
                        :board="props.board"
                        :parent="page.editor" />
                </v-window-item>

            </v-window>
        </v-container>

        <div class="actions">
            <v-btn
                size="small"
                color="primary"
                type="submit"
                :disabled="!page.editor.canSave"
                :loading="page.saving">
                Save
            </v-btn>
            <v-btn
                size="small"
                color="primary"
                @click="applyChange"
                :disabled="!page.editor.canSave"
                :loading="page.applying">
                Apply
            </v-btn>
            <v-btn size="small" color="primary" @click="copy">Copy</v-btn>
            <v-btn size="small" color="none" @click="emit('close')">Cancel</v-btn>
            <v-btn size="small" color="error" v-if="page.widget.id" @click="deleteWidget">
                Delete
            </v-btn>
        </div>
        <vu-confirm ref="confirm" />
    </vu-form>
</template>

<style lang="scss">
.widget-edit {
    h2 {
        font-size: 1.4rem;
        font-weight: normal;
    }
    .v-alert {
        width: 100%;
    }
    .widget-tabs {
        background: rgb(var(--v-theme-background));
        border: solid 1px rgb(var(--v-theme-border));
        .v-tab {
            border-right: solid 1px rgb(var(--v-theme-border));
        }
        .v-tab--selected {
            background: rgb(var(--v-theme-primary));
            color: white;
        }
        .v-btn {
            padding: 0 8px;
            font-size: 11px !important;
        }
    }
    .table-title {
        font-size: 1.4rem !important;
    }
    @media (max-width: 640px) {
        font-size: 12px;
    }
}
</style>
